import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  APP_INITIALIZER,
  EnvironmentProviders,
  inject,
  Optional,
  PLATFORM_ID,
  provideAppInitializer,
  Provider,
  TransferState
} from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { MessageService } from 'primeng/api';

import { Environment } from '@environment';
import { AuthService } from '@usheru-hff/authentication/domain';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { CountryService } from '@usheru-hff/sdk/country';
import { AiInformationService } from '@usheru-hff/sdk/ai-information';
import { LocationService, VIEWER_COUNTRY_KEY } from '@usheru-hff/sdk/location';
import { CacheInterceptor } from '@usheru-hff/shared/cache-interceptor';
import {
  ChannelState,
  CHANNEL_STATE_TOKEN,
  COUNTRY_VERSION_KEY,
  COUNTRY_VERSION_TOKEN,
  VIEWER_COUNTRY_TOKEN
} from '@usheru-hff/shared/tokens';

export function provideBaseServices(environment: Environment): Provider {
  return {
    provide: APP_INITIALIZER,
    useFactory: (
      channelService: ChannelService,
      channelState: ChannelState,
      locationService: LocationService,
      aiInformationService: AiInformationService
    ) => async () => {
      void locationService;
      aiInformationService.init();
      await channelService.init();
      channelState.value = channelService.channel;
    },
    deps: [ChannelService, CHANNEL_STATE_TOKEN, LocationService, AiInformationService],
    multi: true
  };
}

export function provideCacheInterceptor(): Provider {
  return {
    provide: HTTP_INTERCEPTORS,
    useClass: CacheInterceptor,
    multi: true
  };
}

export function provideAuthResource(environment: Environment): Provider {
  return [
    MessageService,
    {
      provide: AuthService,
      useFactory: (
        cookieService: CookieService,
        httpClient: HttpClient,
        router: Router,
        countryService: CountryService,
        messageService: MessageService
      ) => new AuthService(environment, cookieService, router, httpClient, countryService, messageService),
      deps: [CookieService, HttpClient, Router, CountryService, MessageService]
    }
  ];
}

export function provideCountryVersionStateSync(): Provider {
  return {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory:
      (ts: TransferState, platformId: any, countryVersionToken?: string, viewerCountryToken?: string) => () => {
        ts.set(COUNTRY_VERSION_KEY, countryVersionToken ?? null);
        ts.set(VIEWER_COUNTRY_KEY, viewerCountryToken ?? null);
      },
    deps: [TransferState, PLATFORM_ID, [new Optional(), COUNTRY_VERSION_TOKEN], [new Optional(), VIEWER_COUNTRY_TOKEN]]
  };
}

export function provideCountryVersionFromTransferState(): Provider {
  return [
    {
      provide: COUNTRY_VERSION_TOKEN,
      useFactory: (ts: TransferState, platformId: any) => ts.get<string | null>(COUNTRY_VERSION_KEY, null),
      deps: [TransferState, PLATFORM_ID]
    },
    {
      provide: VIEWER_COUNTRY_TOKEN,
      useFactory: (ts: TransferState, platformId: any) => ts.get<string | null>(VIEWER_COUNTRY_KEY, null),
      deps: [TransferState, PLATFORM_ID]
    }
  ];
}
